import React, { useState } from "react"
import { Reorder } from "framer-motion"
import { ChevronDown, ChevronUp, GripVertical, RotateCcw, Settings2, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import TransportTypeIcon from "../TransportTypeIcon"

const ColumnOrderModal = ({ items, defaultItems, onSave, onClose }) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(items)

  const move = (idx, dir) => {
    const j = idx + dir
    if (j < 0 || j >= draft.length) return
    const next = [...draft]
    ;[next[idx], next[j]] = [next[j], next[idx]]
    setDraft(next)
  }

  const handleSave = () => {
    onSave(draft.map((i) => i.id))
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="flex max-h-[80vh] w-full max-w-md flex-col rounded-card bg-surface-main shadow-xl">
        <div className="flex items-center justify-between border-b border-app-content-faint/10 px-4 py-3">
          <h3 className="text-base font-semibold text-app-base-strong">
            {t("admin.columns_order", { defaultValue: "Порядок колонок" })}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-text-muted hover:text-app-content"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="px-4 pt-3 text-xs text-text-muted">
          {t("admin.columns_order_hint", {
            defaultValue: "Перетащите строку или используйте стрелки",
          })}
        </p>

        <Reorder.Group
          as="ul"
          axis="y"
          values={draft}
          onReorder={setDraft}
          className="flex flex-col gap-1.5 overflow-y-auto p-4"
        >
          {draft.map((tt, idx) => (
            <Reorder.Item
              as="li"
              key={tt.id}
              value={tt}
              className="flex cursor-grab items-center gap-2 rounded-input border border-app-content-faint/10 bg-bg-secondary/40 px-2 py-2 active:cursor-grabbing"
            >
              <GripVertical className="h-4 w-4 shrink-0 text-text-muted" />
              <TransportTypeIcon icon={tt.icon_url} name={tt.name} className="h-6 w-6" />
              <span className="min-w-0 flex-1 text-sm text-app-content">{tt.name}</span>
              <button
                type="button"
                disabled={idx === 0}
                onClick={() => move(idx, -1)}
                className="p-1 text-text-secondary hover:text-brand-main disabled:opacity-30"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={idx === draft.length - 1}
                onClick={() => move(idx, 1)}
                className="p-1 text-text-secondary hover:text-brand-main disabled:opacity-30"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </Reorder.Item>
          ))}
        </Reorder.Group>

        <div className="flex items-center justify-between gap-2 border-t border-app-content-faint/10 px-4 py-3">
          <button
            type="button"
            onClick={() => setDraft(defaultItems)}
            className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-brand-main"
          >
            <RotateCcw className="h-4 w-4" />
            {t("admin.reset_default", { defaultValue: "По умолчанию" })}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-button border border-app-content-faint/20 px-4 py-2 text-sm text-text-secondary hover:bg-bg-secondary"
            >
              {t("admin.cancel", { defaultValue: "Отмена" })}
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-button bg-brand-main px-4 py-2 text-sm font-medium text-text-inverse hover:opacity-90"
            >
              {t("admin.save", { defaultValue: "Сохранить" })}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const ColumnOrderButton = ({ items, defaultItems, isDefault, onSave }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-button border border-app-content-faint/20 bg-surface-main px-3 py-1.5 text-sm text-text-secondary transition-colors hover:bg-bg-secondary hover:text-brand-main"
      >
        <Settings2 className="h-4 w-4" />
        {t("admin.columns_order", { defaultValue: "Порядок колонок" })}
        {!isDefault && <span className="h-1.5 w-1.5 rounded-full bg-brand-main" />}
      </button>
      {open && (
        <ColumnOrderModal
          items={items}
          defaultItems={defaultItems}
          onSave={onSave}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}

export default ColumnOrderButton
